import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import BaseApi from "../../../functions/axios";
import { useLocalStorage } from "../../../hooks/localStrage";
import LoginButton from "./LoginButton";

const inputAutofillStyle = {
  "& .MuiInputBase-input": {
    WebkitBoxShadow: "0 0 0 1000px white inset",
  },
};

export default function LoginForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState(0);

  const [, setToken] = useLocalStorage("token");
  const [, setUserId] = useLocalStorage("userId");
  const [, setAdminFlag] = useLocalStorage("adminFlag");
  const [, setMailNotification] = useLocalStorage("mailNotification");
  const [, setCurrentPage] = useLocalStorage("currentPage");

  useEffect(() => {
    const errorAlert = localStorage.getItem("errorAlert");
    if (errorAlert) {
      alert(errorAlert);
      localStorage.removeItem("errorAlert");
    }
  }, []);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");

    // メールアドレスのバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("正しいメールアドレスを入力してください");
      return;
    }

    // パスワードのバリデーション
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
    if (!passwordRegex.test(password)) {
      setError("英字・数字を組み合わせた8文字以上のパスワードを入力してください");
      return;
    }

    try {
      await BaseApi.get("csrf-cookie");
      const postData = { email, password };
      const res = await BaseApi.post("login", postData);

      if (res?.data?.result === 0) {
        const user = res.data.data.user;

        // クライアント保存情報（useLocalStorage経由で統一）
        setToken(res.data.data.access_token);
        setUserId(user.id);
        setAdminFlag(user.admin_flag);
        setMailNotification(user.notification_flag);
        setCurrentPage(process.env.REACT_APP_DASHBOARD_NUM);

        // 要員検索条件や募集検索条件の情報を初期化
        localStorage.removeItem("recuruitmentSearchCondition");
        localStorage.removeItem("PersonnelSearchCriteriaFromRecruitment");
        localStorage.removeItem("PersonnelSearchCriteriaFromConditions");

        if (location.state?.redirect) {
          navigate(location.state.redirect, {
            state: {
              personnelId: location.state.personnelId,
              transitionSorce: "direct",
              projectId: undefined,
            },
          });
        } else {
          navigate("/dashboard");
        }
      } else {
        setStatus(res?.data?.result || -1);
        setError("Emailまたはパスワードに誤りがあります");
      }
    } catch (err) {
      setError("ログイン処理中にエラーが発生しました");
    }
  };

  return (
    <Container component="main" sx={{ p: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          component="form"
          onSubmit={onSubmit}
          noValidate
          maxWidth="300px"
          width="100%"
        >
          <Typography fontWeight="bold" sx={{ fontSize: 16 }}>
            Email
          </Typography>
          <TextField
            variant="standard"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            id="email"
            name="email"
            autoComplete="email"
            autoFocus
            sx={{
              bgcolor: "#ffffff",
              maxWidth: "300px",
              mt: "10px",
              mb: "40px",
              ...inputAutofillStyle,
            }}
            InputProps={{
              disableUnderline: true,
              style: { padding: "20px", fontSize: "14px" },
            }}
          />

          <Box display="flex">
            <Typography fontWeight="bold" sx={{ fontSize: 16 }}>
              Password
            </Typography>
            <Typography sx={{ fontSize: 16 }}>
              &nbsp;※半角英数8文字以上
            </Typography>
          </Box>

          <TextField
            variant="standard"
            margin="normal"
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            name="password"
            id="password"
            autoComplete="current-password"
            sx={{
              bgcolor: "#ffffff",
              maxWidth: "300px",
              mt: "10px",
              mb: "10px",
              ...inputAutofillStyle,
            }}
            InputProps={{
              disableUnderline: true,
              style: { padding: "20px", fontSize: "14px" },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "40px" }}>
            <Link
              href="https://forms.gle/i5LR9GehUaLGmdAv8"
              variant="body2"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontSize: 12, textDecoration: "none" }}
            >
              パスワードを忘れた方はこちら
            </Link>
          </Box>

          {(error || status !== 0) && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mb: "40px" }}
            >
              <Typography
                color="red"
                sx={{ fontSize: 15, display: "flex", alignItems: "center" }}
              >
                <ErrorIcon sx={{ fontSize: 15, mr: 0.5 }} />
                {error || "Emailまたはパスワードに誤りがあります"}
              </Typography>
            </Box>
          )}
          <LoginButton />
        </Box>
      </Box>
    </Container>
  );
}